import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutGrid, FolderOpen, LogOut, Settings, Menu, X } from 'lucide-react';
import NotificationBell from './NotificationBell';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    if (path === '/projects') return location.pathname === '/projects' || location.pathname.startsWith('/projects/');
    return false;
  };

  const displayName = user?.full_name || user?.name || user?.email?.split('@')[0] || 'User';
  const avatarChar = (displayName[0] || 'U').toUpperCase();
  const roleLabel = user?.role
    ? `${user.role === 'ADMIN' ? 'Admin' : 'Viewer'}${user.department ? ` · ${user.department}` : ' · All access'}`
    : 'Viewer · All access';

  return (
    <div className="fp-shell" data-testid="dashboard-shell">
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 h-9 w-9 rounded-md flex items-center justify-center"
        style={{ color: 'var(--fp-txt-2)', background: 'var(--fp-panel)', border: '1px solid var(--fp-line)' }}
        data-testid="open-sidebar-btn"
        aria-label="Open sidebar"
      >
        <Menu className="h-5 w-5" />
      </button>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '248px 1fr',
          minHeight: '100vh',
        }}
        className="fp-shell-grid"
      >
        {/* Sidebar — fixed on mobile drawer, grid item on desktop */}
        <aside
          className={`fp-side fp-sidebar ${mobileOpen ? 'fp-sidebar-open' : ''}`}
          data-testid="sidebar"
        >
          <div className="fp-brand">
            <div className="fp-brand-logo">Fi</div>
            <div className="fp-brand-name"><b>FiPr</b>.ai</div>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden ml-auto"
              style={{ color: 'var(--fp-txt-2)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              data-testid="close-sidebar-btn"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div>
            <div className="fp-nav-label">Workspace</div>
            <nav className="fp-nav" aria-label="Primary">
              <button
                className={`fp-nav-item ${isActive('/dashboard') ? 'active' : ''}`}
                onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
                data-testid="nav-dashboard"
                aria-current={isActive('/dashboard') ? 'page' : undefined}
              >
                <LayoutGrid strokeWidth={1.9} />
                Dashboard
              </button>
              <button
                className={`fp-nav-item ${isActive('/projects') ? 'active' : ''}`}
                onClick={() => { navigate('/projects'); setMobileOpen(false); }}
                data-testid="nav-projects"
                aria-current={isActive('/projects') ? 'page' : undefined}
              >
                <FolderOpen strokeWidth={1.9} />
                Projects
              </button>
            </nav>
          </div>

          <div className="fp-side-spacer" />

          <div className="fp-user-card" data-testid="user-info">
            <div className="fp-user-top">
              <div className="fp-avatar">{avatarChar}</div>
              <div style={{ minWidth: 0 }}>
                <div className="fp-user-name">{displayName}</div>
                <div className="fp-user-mail" title={user?.email}>{user?.email}</div>
              </div>
            </div>
            <div className="fp-role-pill"><span className="dot" />{roleLabel}</div>
            <div className="fp-user-links">
              <button
                onClick={() => { navigate('/dashboard/profile'); setMobileOpen(false); }}
                data-testid="nav-profile-link"
              >
                Edit profile &amp; password
              </button>
              <button
                className="muted"
                onClick={() => { navigate('/dashboard/themes'); setMobileOpen(false); }}
                data-testid="nav-themes-link"
              >
                Theme marketplace
              </button>
              {user?.role === 'ADMIN' && (
                <button
                  className="admin-link"
                  onClick={() => { navigate('/dashboard/beta-requests'); setMobileOpen(false); }}
                  data-testid="nav-beta-requests-link"
                >
                  Beta requests
                </button>
              )}
              <button
                onClick={() => { navigate('/dashboard/profile'); setMobileOpen(false); }}
                data-testid="nav-settings-link"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Settings className="h-3 w-3" strokeWidth={2} />
                Settings
              </button>
            </div>
            <button className="fp-logout" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-3.5 w-3.5" strokeWidth={2} />
              Log out
            </button>
          </div>
        </aside>

        {/* Main */}
        <div style={{ position: 'relative', minWidth: 0 }} className="fp-main-wrap">
          <div style={{ position: 'absolute', top: 26, right: 190, zIndex: 30 }}>
            <NotificationBell projectId={null} />
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
